 
import AnchorMenu from '../components/AnchorMenu'
import { client } from '../src/App'
import { Spinner } from '@contentful/f36-spinner'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import ProductOwners from '../modules/ProductOwners'
import Faq from '../modules/Faq'
import { isUpperCase, slugify } from '../functions/utils'
import NewExtensions from '../modules/NewExtensions'
import Ship from '../modules/Ship'
import EntryRequirements from '../modules/EntryRequirements'
import ArrivalInformation from '../modules/ArrivalInformation'
import Hotel from '../modules/Hotel'
import Flights from '../modules/Flights'
import ExpeditionGear from '../modules/ExpeditionGear'
import ActivityOpportunity from '../modules/ActivityOpportunity'
import ExpeditionGuide from '../modules/ExpeditionGuide'
import Marketing from '../modules/Marketing'
import ExtensionOperatingDates from '../modules/ExtensionOperatingDates'
import Itinerary from '../modules/Itinerary'
import ExtensionDetails from '../modules/ExtensionDetails'
import ChartEmbed from '../modules/ChartEmbed'
import Deviations from '../modules/Deviations'
import ErrorBoundary from '../components/ErrorBoundary'

const Content = () => {
  const { id, extensionId } = useParams()
  const [ anchorData, setAnchorData ] = useState([])
  const [ error, setError ] = useState(false)

  const fetcher = async () => {
    setError(false)
    try {
      const entryItems = await client.getEntries({
        content_type: id ? 'testProduct' : 'extension', 
        'fields.slug[in]': id || extensionId,
        include: 10
      })
      const item = entryItems.items[0]
      if (!item) {
        setError(true)
        return
      }
      const { items } = await client.getEntries({ links_to_entry: item.sys.id, include: 3 })

      const { productOwners, entryRequirements, expeditionGear, FAQS, expeditionGuide, salesMarketing } = items && items.length && items[0].fields
      const { name, deviations, ship, activityOpportunity, arrivalInformation, hotels, hotelsChartEmbed, flights, flightsChartEmbed, departureInformation, extensions, itinerary, itineraryFreeText } = item.fields

      const fieldsObj = {
        name,
        ...(productOwners && { productOwners }),
        ...(itinerary && { itinerary }),
        ...(itinerary && itineraryFreeText && { itineraryFreeText }),
        ...(deviations && { deviations }),
        ...(activityOpportunity && { activityOpportunity }),
        ...(ship && { ship }),
        ...(entryRequirements && { entryRequirements }),
        ...(arrivalInformation && { arrivalInformation }),
        ...(hotels && { hotels }),
        ...(hotelsChartEmbed && { hotelsChartEmbed }),
        ...(flights && { flights }),
        ...(flightsChartEmbed && { flightsChartEmbed }),
        ...(departureInformation && { departureInformation }),
        ...(expeditionGear && { expeditionGear }),
        ...(extensions && { extensions }),
        ...(FAQS && { FAQS }),
        ...(expeditionGuide && { expeditionGuide }),
        ...(salesMarketing && { salesMarketing }),
      }
      const fields = item.sys.contentType.sys.id === 'extension' ? {
        ...(item.fields.productOwners && { productOwners: item.fields.productOwners }),
        ...item.fields,
      } : fieldsObj

      const modules = {
        content: [],
      }
  
      for (let [key, value] of Object.entries(fields)) {
        const subSectionList = ['entryRequirements', 'arrivalInformation', 'departureInformation']
        const subSectionExclusions = ['internalName', 'additionalInfo', 'arrivals', 'inclusions']
        const subSections = []
        if (subSectionList.includes(key)) {
          value && value.forEach(item => {
            if (item.fields) {
              Object.keys(item.fields).forEach((subKey, i) => {
                if (!subSectionExclusions.includes(subKey)) {
                  if (subKey === 'arrivalInformation') {
                    const name = Object.values(item.fields)[i]
                    const id = slugify(name)
                    subSections.push({
                      name,
                      id: /^[A-Za-z]/.test(id) ? id : 'x' + id,
                    })
                  } else {
                    subSections.push({
                      name: isUpperCase(subKey) ? subKey : subKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                      id: subKey === 'additionalInfo' ?  key + subKey : subKey,
                    })
                  }
                }
              })
            }
          })
        }
        modules.content.push({
          name: isUpperCase(key) ? key : key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
          id: key,
          subSections,
        })
      }
  
      setData(fields)
      setAnchorData(modules.content)
    } catch (err) {
      console.log(err)
      setError(true)
    }
  }

  const [ data, setData ] = useState()
  useEffect(() => {
    fetcher()
    setActiveAnchor('productOwners')
  }, [id, extensionId])

  useEffect(() => {
    if (data) {
      window.scrollTo({ top: 0 })
      document.title = data.name

      const hash = window.location.hash
      const anchorEl = hash && document.querySelector(hash)
      if (anchorEl) {
        setTimeout(() => {
          anchorEl.scrollIntoView()
        }, 200)
      }
    }
  }, [data])

  const [ activeAnchor, setActiveAnchor ] = useState('productOwners')

  useEffect(() => {
    if (data) {
      const observerOptions = {
        rootMargin: '50px 0px 50px 0px',
        threshold: [0.1]
      }
      const observer = new IntersectionObserver(observerCallback, observerOptions)
      function observerCallback(entries) {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            //console.log(entry.target.id, entry, window.scrollY)
            if (window.scrollY !== 0) setActiveAnchor(entry.target.id)
          }
        })
      }
      setTimeout(() => {
        const collection = document.querySelectorAll('#moduleSection > article')
        collection.forEach((i) => {
          if (i) {
            observer.observe(i)
          }
        })
      }, 1000)

    }
  }, [data])

  if (error) return <h1 style={{margin:'auto'}}>404 Not Found</h1>
  if (!data) return <Spinner size="large" />
  return (
    <>
      <section id="moduleSection" className="module-section">
          <div style={{marginBottom: 40}}>
            <h1 className='content_h1' style={{marginBottom: 15}}>{data.name}</h1>
          </div>
          <ErrorBoundary fallbackMessage="Unable to load Deviations section">
            {data.deviations && <Deviations data={data.deviations} />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Product Owners section">
            {data.productOwners && <ProductOwners data={data.productOwners} slug="productOwners" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Itinerary section">
            {data.itinerary && <Itinerary key={data.itinerary.sys.urn} data={data.itinerary.sys.urn} freeText={data.itineraryFreeText} slug="itinerary" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Extension Itinerary section">
            {data.extensionDetails && data.extensionDetails.length && <ExtensionDetails key={data.name} data={data.extensionDetails} slug="extensionItinerary" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Extension Operating Dates section">
            {(data.extensionOperatingDates || data.extensionOperatingDatesText) && <ExtensionOperatingDates embed={data.extensionOperatingDates} text={data.extensionOperatingDatesText} slug="extensionOperatingDates" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Activity Opportunity section">
            {data.activityOpportunity && <ActivityOpportunity data={data.activityOpportunity} slug="activityOpportunity" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Ship section">
            {data.ship && <Ship data={data.ship} slug="ship" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Entry Requirements section">
            {data.entryRequirements && <EntryRequirements data={data.entryRequirements} slug="entryRequirements" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Arrival Information section">
            {data.arrivalInformation && <ArrivalInformation data={data.arrivalInformation} slug="arrivalInformation" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Hotels section">
            {data.hotels && <Hotel data={data.hotels} slug="hotels" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Hotels Chart section">
            {data.hotelsChartEmbed && <ChartEmbed data={data.hotelsChartEmbed} slug="hotelsChartEmbed" title="Hotel" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Flights section">
            {data.flights && <Flights data={data.flights} slug="flights" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Flights Chart section">
            {data.flightsChartEmbed && <ChartEmbed data={data.flightsChartEmbed} slug="flightsChartEmbed" title="Flights" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Departure Information section">
            {data.departureInformation && <ArrivalInformation data={data.departureInformation} slug="departureInformation" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Expedition Gear section">
            {data.expeditionGear && <ExpeditionGear data={data.expeditionGear} slug="expeditionGear" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Extensions section">
            {data.extensions && (
              <div id='extensions'>
                {data.extensions.map((ext, i) => {
                  if (!ext.fields) return
                  const { extension, disclaimer, extensions, additionalInfo } = ext.fields
                  return (
                    <ErrorBoundary key={i} fallbackMessage="Unable to load the extension">
                      <div style={{marginTop: 60}}>
                        <NewExtensions data={extension} disclaimer={disclaimer} slug={extensions} freeText={additionalInfo} />
                      </div>
                    </ErrorBoundary>
                  )
                })}
              </div>
            )}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load FAQs section">
            {data.FAQS && (
              data.FAQS.map((faq, i) => {
                if (!faq.fields) return
                // TOASK faq.fields.FAQs
                return (
                  <ErrorBoundary key={i} fallbackMessage="Unable to load this FAQ">
                    <Faq data={faq.fields.questionanswer} slug="FAQS" />
                  </ErrorBoundary>
                )
              })
            )}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Expedition Guide section">
            {data.expeditionGuide && <ExpeditionGuide data={data.expeditionGuide} slug="expeditionGuide" />}
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="Unable to load Sales & Marketing section">
            {data.salesMarketing && <Marketing data={data.salesMarketing} slug="salesMarketing" />}
          </ErrorBoundary>
      </section>
      <ErrorBoundary fallbackMessage="Unable to load navigation menu">
        <AnchorMenu data={anchorData} activeAnchor={activeAnchor} setActiveAnchor={setActiveAnchor} />
      </ErrorBoundary>
    </>
  )
}

export default Content